import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import useOpenApiClient from '@/hooks/useOpenApiClient';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'wouter';
import ReviewRequestConfirmationIcon from '@/assets/icons/review_request_confirmation.svg?react';
import CheckFilled from '@/assets/icons/check_filled.svg?react';
import CheckEmpty from '@/assets/icons/check_empty.svg?react';
import Check from '@/assets/icons/check.svg?react';
import { useState } from 'react';
import { components } from '@/types/api';
import { cn } from '@/lib/utils';

interface RequestReviewSheetProps {
  open: boolean;
  onClose: () => void;
}

const ISSUE_TYPE_VALUES = [
  'incorrect_line_total',
  'incorrect_total',
  'missing_line_items',
  'mismatched_line_items',
  'incorrect_item_description',
  'incorrect_date',
  'incorrect_store_name',
] as const;
export const ISSUE_TYPE_DISPLAY_LABELS = {
  incomplete_data: 'Incomplete data',
  incorrect_date: 'Incorrect date',
  incorrect_item_description: 'Incorrect item description',
  incorrect_line_total: 'Incorrect line total',
  incorrect_store_name: 'Incorrect store name',
  incorrect_total: 'Incorrect total',
  mismatched_line_items: 'Mismatched line items',
  missing_line_items: 'Missing line items',
  missing_receipt_details: 'Missing receipt details',
  suspected_fraud: 'Suspected fraud',
};

export function RequestReviewSheet(props: RequestReviewSheetProps) {
  const { receiptId } = useParams<'/receipts/:receiptId'>();
  const queryClient = useQueryClient();
  const { apiFetch } = useOpenApiClient();
  const [reasons, setReasons] = useState<string[]>([]);
  const requestReviewMutation = useMutation({
    mutationKey: ['v1', 'receipts', receiptId, 'review'],
    mutationFn: async (values: components['schemas']['ReceiptReviewBodyInput']) =>
      apiFetch.POST('/receipts/{receipt_id}/review', {
        params: {
          path: {
            receipt_id: receiptId,
          },
        },
        body: values,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['v1', 'receipts'],
        refetchType: 'active',
      });
    },
  });
  const toggleReason = (reason: string) => {
    const temp = [...reasons];
    if (reasons.includes(reason)) {
      const index = temp.findIndex((r) => r === reason);
      temp.splice(index, 1);
      setReasons([...temp]);
      return;
    }
    temp.push(reason);
    setReasons([...temp]);
  };
  return (
    <Sheet open={props.open} onOpenChange={props.onClose}>
      <SheetContent side="bottom" className="bg-white pt-4 pb-7">
        {requestReviewMutation.isIdle ? (
          <>
            <SheetHeader className="space-y-1 px-1.5 pb-2 text-left">
              <SheetTitle className="flex items-center justify-between py-4">
                <span className="text-foreground text-2xl font-normal">Request a review</span>
              </SheetTitle>
              <p className="text-foreground text-sm leading-5 font-normal opacity-70">
                Tell us what&apos;s wrong with the receipt and our Enrich team member will review
                and get back to you.{' '}
              </p>
            </SheetHeader>
            <div className="border-primary-dark/10 flex flex-wrap gap-2 border-t py-2">
              {ISSUE_TYPE_VALUES.map((item) => (
                <Button
                  className={cn(
                    'border-primary-dark/20 h-fit rounded-[20px] border px-2 py-1.5 text-sm',
                    {
                      'font-normal': !reasons.includes(item),
                      'font-bold': reasons.includes(item),
                    },
                  )}
                  onClick={() => toggleReason(item)}
                  variant="bordered"
                  key={item}
                >
                  {reasons.includes(item) ? <CheckFilled /> : <CheckEmpty />}{' '}
                  {ISSUE_TYPE_DISPLAY_LABELS[item]}
                </Button>
              ))}
            </div>
            <div className="text-center">
              <Button
                variant="enrich_dialog"
                className="bg-primary mx-2 my-5 flex-1 self-center text-white"
                onClick={() => requestReviewMutation.mutate({ issue_types: reasons })}
                disabled={!reasons.length}
              >
                Submit Request
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center pt-12 pb-6">
              <ReviewRequestConfirmationIcon />
              <div className="text-primary-dark/70 flex items-center justify-center gap-2 pt-6 pb-3 text-base font-normal">
                <Check />
                Request sent
              </div>
              <p className="text-primary-dark/70 flex flex-col gap-2 px-2 pt-6 text-center text-sm leading-5 font-normal">
                A member of the Enrich Network team will review the receipt.
                <br />
                <span className="font-bold">
                  Reference number: {requestReviewMutation.data?.data?.data?.ID}
                </span>
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
